/**
 * − and + beside every number field in the Inspector (operator, 2026-10-15:
 * "- and + on numbers", from the 2026-10-05 proposal's steppers). On the iPad
 * there are no spin arrows and no arrow keys, so a channel or a velocity
 * could only be changed by tapping into the field and typing.
 *
 * ONE MECHANISM, NOT FORTY EDITS. The panel has ~45 number fields built in
 * several shapes (plain controlled inputs, NumberField, SendBrowser), each
 * with its own commit rule. Rather than rewrite every one, the panel installs
 * this once: it finds each `label.inspector-field > input[type=number]`,
 * puts a − before it and a + after it, and a press does exactly what the
 * keyboard's arrow keys do - `stepDown()` / `stepUp()`, which honour the
 * field's own min / max / step - then fires the same `input` and `change`
 * events typing would. Every field's existing handler commits the value, so
 * no field's rules are bypassed. A field added later gets them for free.
 *
 * Not stepped: Width / Height (`.inspector-dim-field`, not `.inspector-field`
 * - 1px a press is no use) and anything marked `data-no-step`.
 *
 * Holding a button repeats (after 400ms, every 70ms), so 1 to 100 is one
 * press, not ninety-nine. The buttons are not in the tab order: a keyboard
 * already has the arrow keys in the field.
 */

const SELECTOR = [
  'label.inspector-field > input[type="number"]',
  // A number that shares its field with a button (the Send / CC browse
  // arrow): the − and + go around the whole field, not inside it.
  'label.inspector-field > .inspector-step-host > input[type="number"]',
].map((s) => `${s}:not([data-no-step]):not([data-stepped])`).join(', ');

function step(input: HTMLInputElement, dir: 1 | -1): void {
  if (input.disabled || input.readOnly) return;
  const before = input.value;
  try {
    if (dir > 0) input.stepUp(); else input.stepDown();
  } catch {
    return; // an input that cannot step (no valid value and no min) is left alone
  }
  if (input.value === before) return;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function makeButton(input: HTMLInputElement, dir: 1 | -1, label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'inspector-step';
  b.dataset.dir = dir > 0 ? 'up' : 'down';
  b.tabIndex = -1;
  b.textContent = dir > 0 ? '+' : '−';
  b.setAttribute('aria-label', `${dir > 0 ? 'Increase' : 'Decrease'} ${label}`);
  let delay: number | undefined;
  let repeat: number | undefined;
  const stop = () => {
    if (delay !== undefined) clearTimeout(delay);
    if (repeat !== undefined) clearInterval(repeat);
    delay = repeat = undefined;
  };
  b.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault(); // keep focus where it is; do not start a text selection
    step(input, dir);
    stop();
    delay = window.setTimeout(() => { repeat = window.setInterval(() => step(input, dir), 70); }, 400);
  });
  for (const ev of ['pointerup', 'pointerleave', 'pointercancel'] as const) b.addEventListener(ev, stop);
  return b;
}

function enhance(input: HTMLInputElement): void {
  input.dataset.stepped = 'true';
  const host = (input.parentElement?.classList.contains('inspector-step-host') ? input.parentElement : input) as HTMLElement;
  const label = input.closest('label')?.querySelector(':scope > span')?.textContent?.trim() || 'value';
  host.before(makeButton(input, -1, label));
  host.after(makeButton(input, 1, label));
}

/** Install on the panel's root; returns the uninstall. */
export function installInspectorSteppers(root: HTMLElement): () => void {
  const scan = () => root.querySelectorAll<HTMLInputElement>(SELECTOR).forEach(enhance);
  scan();
  const mo = new MutationObserver(scan);
  mo.observe(root, { childList: true, subtree: true });
  return () => mo.disconnect();
}
